import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledInput from 'src/components/form/controlled-input';
import ControlledSelect from 'src/components/form/controlled-select';
import { ControlledSwitch } from 'src/components/form/controlled-switch/ControlledSwitch';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { FormField } from 'src/components/form/form/FormField';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';

import { emptyFilterQuery } from '@/utils/collectionUtils';

import type { WidgetDataSource } from '../../gigawidget/types';
import { WidgetPropertyFilter } from '../../universal-widget/form/WidgetPropertyFilter';
import type { EnterpriseRiskHeatmapSettings } from './enterpriseRiskHeatmapSettingsSchema';
import {
  enterpriseRiskHeatmapAggregations,
  enterpriseRiskHeatmapSettingsSchema,
} from './enterpriseRiskHeatmapSettingsSchema';

type FieldsProps<TDataSource extends WidgetDataSource> = {
  dataSource: TDataSource;
};

const EnterpriseRiskHeatmapSettingsFormFields = <
  TDataSource extends WidgetDataSource,
>({
  dataSource,
}: FieldsProps<TDataSource>) => {
  const { control, watch } = useFormContext<EnterpriseRiskHeatmapSettings>();
  const { t } = useTranslation('common', {
    keyPrefix: 'dashboard.widgetSettings.fields',
  });
  const { t: at } = useTranslation('common', {
    keyPrefix: 'dashboard.widgetSettings.fields.aggregationOptions',
  });

  const customTitle = watch('customTitle');

  return (
    <div>
      <ControlledSelect
        testId={'aggregation'}
        name={'aggregation'}
        label={t('aggregation')}
        control={control}
        options={enterpriseRiskHeatmapAggregations.map((value) => ({
          value,
          label: at(value),
        }))}
      />
      <Controller
        control={control}
        render={({ field: { value, onChange } }) => (
          <>
            <FormField label={t('filtering')}>
              <WidgetPropertyFilter
                value={value}
                onChange={onChange}
                dataSource={dataSource}
              />
            </FormField>
            <div className={'mb-4'}>
              <ControlledSwitch
                name={'customTitle'}
                label={t('customTitle')}
                control={control}
              />
            </div>
            {customTitle && (
              <ControlledInput
                control={control}
                name={'title'}
                label={t('customTitle')}
              />
            )}
          </>
        )}
        name={'filtering'}
      />
    </div>
  );
};

type Props<TDataSource extends WidgetDataSource> = {
  onDismiss: () => void;
  onSave: (data: EnterpriseRiskHeatmapSettings) => Promise<void>;
  dataSource: TDataSource;
};

export const EnterpriseRiskHeatmapSettingsModal = <
  TDataSource extends WidgetDataSource,
>({
  onDismiss,
  onSave,
  dataSource,
}: Props<TDataSource>) => {
  const { t } = useTranslation('common');
  const [settings] =
    useDashboardWidgetSettings<EnterpriseRiskHeatmapSettings>();

  const values: EnterpriseRiskHeatmapSettings = {
    filtering: emptyFilterQuery,
    customTitle: false,
    aggregation: 'mean',
    ...(settings ?? {}),
  };

  return (
    <CustomisableForm
      onSave={onSave}
      defaultValues={values}
      formId={'enterprise-risk-heatmap-form'}
      schema={enterpriseRiskHeatmapSettingsSchema}
      i18n={t('widget')}
      renderTemplate={(renderProps) => (
        <ModalWrapper {...renderProps} visible={true} />
      )}
      onDismiss={onDismiss}
    >
      <EnterpriseRiskHeatmapSettingsFormFields dataSource={dataSource} />
    </CustomisableForm>
  );
};
